import { useEffect } from "react";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ReactCountryFlag from "react-country-flag";
import { Controller, useForm } from "react-hook-form";
import { useModals } from "../../../context/ModalContext";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import COUNTRIES from "../../../data/countriesList.json";
import { useAppSelector } from "../../../redux/hooks/redux";
import { menuPropsModal } from "../../../utils";
import { PhoneInputWithFlags } from "../../common/PhoneInputWithFlags";
import { SASInputStyles, SASSelectStyles } from "../../ui/styles/inputStyles";
import { PortingFormData } from "./hooks/interfaces/porting.interface";
import { usePhoneRequestSendMutation } from "./hooks/usePhoneRequestSendMutation";
import { LabelWithHelp } from "./LabelWithHelp";
import { buildE164 } from "../utils/buildE164";
import { selectCurrentCompany } from "../../../redux/features/user/selectors/userState.selectors";
import { colors } from "../../ui/colors";

interface PhoneAccessRequestProps {
  refetch?: () => void;
  hideActions?: boolean;
  onFormChange?: (data: PortingFormData) => void;
}

const SectionCard = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "16px",
        p: 2,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 800,
            color: colors.gray[500],
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>

        {description ? (
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 12.5,
              fontWeight: 500,
              color: colors.gray[400],
              lineHeight: 1.4,
            }}
          >
            {description}
          </Typography>
        ) : null}
      </Box>

      {children}
    </Paper>
  );
};

const Label = ({ text }: { text: string }) => (
  <Typography
    sx={{
      color: colors.gray[500],
      fontWeight: 700,
      fontSize: 12.5,
      mb: 0.75,
    }}
  >
    {text}
  </Typography>
);

const SidebarInfoCard = () => {
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1.5px solid ${colors.gray[100]}`,
        borderRadius: "16px",
        p: 2,
        bgcolor: colors.gray[50],
      }}
    >
      <Box sx={{ display: "flex", gap: 1 }}>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: colors.orange[600] }} />

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 800,
              color: colors.gray[500],
            }}
          >
            Porting is semi-automated
          </Typography>

          <Typography
            sx={{
              mt: 0.5,
              fontSize: 12.5,
              fontWeight: 500,
              color: colors.gray[400],
              lineHeight: 1.45,
            }}
          >
            Your request may require verification from your current carrier. Make
            sure the account number, PIN, and billing information match your
            carrier records.
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const PortingChecklistCard = () => {
  const items = [
    "Current carrier name",
    "Account number",
    "Transfer PIN",
    "Billing address",
    "Authorized contact details",
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1.5px solid ${colors.gray[100]}`,
        borderRadius: "16px",
        p: 2,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 800,
          color: colors.gray[500],
        }}
      >
        Before you continue
      </Typography>

      <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.9 }}>
        {items.map((item) => (
          <Typography
            key={item}
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: colors.gray[400],
              lineHeight: 1.3,
            }}
          >
            • {item}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
};

const SetupFeeCard = () => {
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1.5px solid ${colors.gray[100]}`,
        borderRadius: "16px",
        p: 2,
        boxShadow: `0 2px 12px ${colors.orange[400]}12`,
      }}
    >
      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 800,
          color: colors.gray[500],
        }}
      >
        Setup fee
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          fontSize: 12.5,
          fontWeight: 500,
          color: colors.gray[400],
          lineHeight: 1.4,
        }}
      >
        A one-time setup fee is required to activate phone number setup for this
        plan.
      </Typography>

      <Divider sx={{ my: 1.5, borderColor: colors.gray[100] }} />

      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: 700,
          color: colors.gray[400],
        }}
      >
        In the setup flow, payment is handled by the external checkout button.
      </Typography>
    </Paper>
  );
};

export const PhoneAccessRequest = ({
  refetch,
  hideActions = false,
  onFormChange,
}: PhoneAccessRequestProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<PortingFormData>({
    defaultValues: {
      companyName: "",
      legalRepresentative: "",
      email: "",

      phoneNumber: "",
      phoneNumberCountryCode: "US",

      taxidorein: "",
      street1: "",
      street2: "",
      externalNumber: "",
      internalNumber: "",
      neighborhood: "",
      city: "",
      state: "",
      postalCode: "",
      issuingCountry: "USA",
      country: "MX",
      documentType: "ID Card",
      identityNumber: "",
      pin: "",

      numberToPort: "",
      numberToPortCountryCode: "US",

      currentCarrier: "",
      accountNumber: "",
      notes: "",
    },
  });

  const { showSnackbar } = useGlobalSnackbar();
  const { closeByKey } = useModals();
  const company = useAppSelector(selectCurrentCompany);

  const { sendRequestAsync, isLoading } = usePhoneRequestSendMutation();

  const toE164OrOriginal = (
    phoneNumber?: string,
    countryCode?: string,
  ): string => {
    if (!phoneNumber) return "";

    try {
      return buildE164(phoneNumber, countryCode);
    } catch {
      return phoneNumber;
    }
  };

  const buildPortingPayload = (data: PortingFormData): PortingFormData => {
    const customerPhoneNumberE164 = toE164OrOriginal(
      data.phoneNumber,
      data.phoneNumberCountryCode,
    );

    const numberToPortE164 = toE164OrOriginal(
      data.numberToPort,
      data.numberToPortCountryCode,
    );

    return {
      ...data,

      /**
       * Inverted payload:
       * - phoneNumber = number being ported
       * - numberToPort = customer/contact phone number
       */
      phoneNumber: numberToPortE164,
      numberToPort: customerPhoneNumberE164,
    };
  };

  useEffect(() => {
    if (!onFormChange) return;

    const subscription = watch((value) => {
      const formData = value as PortingFormData;
      const payload = buildPortingPayload(formData);

      onFormChange(payload);
    });

    return () => subscription.unsubscribe();
  }, [watch, onFormChange]);

  const onSubmit = async (data: PortingFormData) => {
    try {
      const payload = buildPortingPayload(data);

      await sendRequestAsync({
        ...payload,
        externalUid: Number(company.id),
      });

      await refetch?.();

      showSnackbar(
        "Porting request submitted successfully",
        "success",
        5000,
        "bottom-left",
      );

      closeByKey("Configure-Vr-Assistant");
      reset();
    } catch (error: any) {
      showSnackbar(
        error?.response?.data?.message?.[0] ||
        "Error submitting porting request",
        "error",
        6000,
        "bottom-left",
      );
    }
  };

  return (
    <Box sx={{ minHeight: "100%", bgcolor: "var(--fx-surface-base, #FFFFFF)" }}>
      <Box sx={{ mx: "auto" }}>
        <Box sx={{ mb: 3.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 0.75,
            }}
          >
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 800,
                color: colors.gray[500],
                letterSpacing: "-0.03em",
              }}
            >
              Port existing phone number
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: 14,
              color: colors.gray[400],
              fontWeight: 500,
              maxWidth: 720,
              lineHeight: 1.45,
            }}
          >
            Complete the information required to request the portability of your
            current business phone number.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 290px" },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <SectionCard
              title="Porting information"
              description="Enter the phone number and carrier details from your current provider."
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Label text="Number to port" />
                  <PhoneInputWithFlags
                    name="numberToPort"
                    countryCodeName="numberToPortCountryCode"
                    control={control}
                    errors={errors}
                    setValue={setValue}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label text="Current carrier" />
                  <Controller
                    name="currentCarrier"
                    control={control}
                    rules={{ required: "Current carrier is required" }}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        placeholder="E.g. Verizon"
                        variant="outlined"
                        error={!!errors.currentCarrier}
                        helperText={errors.currentCarrier?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label text="Account number" />
                  <Controller
                    name="accountNumber"
                    control={control}
                    rules={{ required: "Account number is required" }}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        placeholder="Carrier account number"
                        variant="outlined"
                        error={!!errors.accountNumber}
                        helperText={errors.accountNumber?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label text="PIN" />
                  <Controller
                    name="pin"
                    control={control}
                    rules={{ required: "PIN is required" }}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        placeholder="Transfer PIN"
                        variant="outlined"
                        error={!!errors.pin}
                        helperText={errors.pin?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard
              title="Customer information"
              description="This information should match what your current carrier has on file."
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LabelWithHelp
                    label="Company name"
                    helpText="Enter the name or company name registered with your current carrier"
                  />
                  <Controller
                    name="companyName"
                    control={control}
                    rules={{ required: "Company name is required" }}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        placeholder="E.g. Acme Telecom S.A."
                        variant="outlined"
                        error={!!errors.companyName}
                        helperText={errors.companyName?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label text="Email address" />
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        type="email"
                        placeholder="E.g. john@company.com"
                        variant="outlined"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label text="Phone number" />
                  <PhoneInputWithFlags
                    name="phoneNumber"
                    countryCodeName="phoneNumberCountryCode"
                    control={control}
                    errors={errors}
                    setValue={setValue}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LabelWithHelp
                    label="Tax ID or EIN"
                    helpText="Enter the Tax ID or EIN registered with your current carrier"
                  />
                  <Controller
                    name="taxidorein"
                    control={control}
                    rules={{ required: "Tax ID or EIN is required" }}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        placeholder="E.g. 88-2987635"
                        variant="outlined"
                        error={!!errors.taxidorein}
                        helperText={errors.taxidorein?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard
              title="Billing address"
              description="Use the billing address associated with your current carrier account."
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Label text="Address line 1" />
                  <Controller
                    name="street1"
                    control={control}
                    rules={{ required: "Address line 1 is required" }}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        placeholder="Street address"
                        variant="outlined"
                        error={!!errors.street1}
                        helperText={errors.street1?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Label text="Address line 2" />
                  <Controller
                    name="street2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        placeholder="Apartment, suite, unit, etc."
                        variant="outlined"
                        error={!!errors.street2}
                        helperText={errors.street2?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label text="City" />
                  <Controller
                    name="city"
                    control={control}
                    rules={{ required: "City is required" }}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        placeholder="City"
                        variant="outlined"
                        error={!!errors.city}
                        helperText={errors.city?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label text="State" />
                  <Controller
                    name="state"
                    control={control}
                    rules={{ required: "State is required" }}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        placeholder="State"
                        variant="outlined"
                        error={!!errors.state}
                        helperText={errors.state?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label text="Country" />
                  <Controller
                    name="country"
                    control={control}
                    rules={{ required: "Country is required" }}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        error={!!errors.country}
                        sx={SASSelectStyles}
                      >
                        <Select
                          {...field}
                          MenuProps={menuPropsModal}
                          variant="outlined"
                          renderValue={(selected) => {
                            const country = COUNTRIES.find(
                              (c) => c.code === selected,
                            );

                            return country ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <ReactCountryFlag
                                  svg
                                  countryCode={country.code}
                                  style={{ width: "1.5em" }}
                                />
                                <span>{country.name}</span>
                              </Box>
                            ) : (
                              selected
                            );
                          }}
                        >
                          {COUNTRIES.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <ReactCountryFlag
                                  svg
                                  countryCode={country.code}
                                  style={{ width: "1.5em" }}
                                />
                                <span>{country.name}</span>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label text="Zip code" />
                  <Controller
                    name="postalCode"
                    control={control}
                    rules={{ required: "Zip code is required" }}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        placeholder="Zip code"
                        variant="outlined"
                        error={!!errors.postalCode}
                        helperText={errors.postalCode?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard
              title="Government issued ID"
              description="Provide identity details for the authorized person on the account."
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Label text="Issuing country / region" />
                  <Controller
                    name="issuingCountry"
                    control={control}
                    rules={{ required: "Issuing country is required" }}
                    render={({ field }) => (
                      <FormControl
                        sx={SASSelectStyles}
                        fullWidth
                        error={!!errors.issuingCountry}
                      >
                        <Select
                          MenuProps={menuPropsModal}
                          {...field}
                          variant="outlined"
                        >
                          <MenuItem value="USA">
                            United States of America
                          </MenuItem>
                          <MenuItem value="MX">Mexico</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label text="Identity type" />
                  <Controller
                    name="documentType"
                    control={control}
                    rules={{ required: "Document type is required" }}
                    render={({ field }) => (
                      <FormControl
                        sx={SASSelectStyles}
                        fullWidth
                        error={!!errors.documentType}
                      >
                        <Select
                          MenuProps={menuPropsModal}
                          {...field}
                          variant="outlined"
                        >
                          <MenuItem value="DNI">Driver's License</MenuItem>
                          <MenuItem value="Passport">Passport</MenuItem>
                          <MenuItem value="ID Card">National ID</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label text="Identity number" />
                  <Controller
                    name="identityNumber"
                    control={control}
                    rules={{ required: "Document number is required" }}
                    render={({ field }) => (
                      <TextField
                        sx={SASInputStyles}
                        {...field}
                        fullWidth
                        placeholder="E.g. 1234567"
                        variant="outlined"
                        error={!!errors.identityNumber}
                        helperText={errors.identityNumber?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard title="Additional notes">
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    sx={SASInputStyles}
                    {...field}
                    fullWidth
                    placeholder="Add anything your current carrier may require..."
                    variant="outlined"
                    multiline
                    rows={4}
                  />
                )}
              />
            </SectionCard>

            {!hideActions && (
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<RestartAltIcon />}
                  onClick={() => reset()}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 700,
                  }}
                >
                  Clear
                </Button>

                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  onClick={handleSubmit(onSubmit)}
                  disabled={isLoading}
                  sx={{
                    bgcolor: colors.orange[500],
                    color: "white",
                    boxShadow: "none",
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 800,
                    px: 2.4,
                    "&:hover": {
                      bgcolor: colors.orange[600],
                      boxShadow: "none",
                    },
                  }}
                >
                  {isLoading ? "Submitting..." : "Submit request"}
                </Button>
              </Stack>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SidebarInfoCard />
            <PortingChecklistCard />
            <SetupFeeCard />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};